import React, { createContext, useContext, useMemo, useState } from 'react'

export type SummaryTableContextType = {
  showSummaryTable: boolean
  toggleSummaryTable: () => void
}

export const SummaryTableContext = createContext<SummaryTableContextType>({
  showSummaryTable: false,
  toggleSummaryTable: () => { },
})

export const useSummaryTableContext = () => {
  const context = useContext(SummaryTableContext)

  if (context === undefined) {
    throw new Error(
      'useSummaryTableContext must be used within a SummaryTableContextProvider',
    )
  }

  return context
}

/**
 * The SummaryTableContextProvider is a context provider that help share the state
 *    between the SummaryTable and the toolbar and the additional toolbars components through
 *    the Mosaic Window rendering. Thus, any local actions or state that need to be shared
 *    between these two components should be managed here.
 */
export type SummaryTableContextProps = {
  children?: React.ReactNode
}

export const SummaryTableContextProvider: React.FC<SummaryTableContextProps> = ({ children }) => {
  const [showSummaryTable, setShowSummaryTable] = useState(false)

  const contextValue = useMemo(() => {
    const toggleSummaryTable = () => {
      setShowSummaryTable((prev) => !prev)
    }

    return {
      showSummaryTable,
      toggleSummaryTable,
    }
  }, [showSummaryTable])

  return (
    <SummaryTableContext.Provider value={contextValue}>
      {children}
    </SummaryTableContext.Provider>
  )
}
